/**
 * Human-readable labels and visual metadata for NER entity types.
 * Used by MentionHoverCard (report / data-items / corpus tooltips) and
 * EntityRecognitionTaskCard (AI Assistant link panel) so both surfaces
 * show consistent friendly names.
 */
export const TYPE_META = {
    GPE:     {label: 'Place',        desc: 'Geopolitical Entity — countries, cities, states and other political regions',        color: '#5a7fa8', bg: '#eef3f8', initial: 'P'},
    LOC:     {label: 'Place',        desc: 'Location — natural places that are not geopolitical (oceans, mountain ranges, regions)', color: '#5a7fa8', bg: '#eef3f8', initial: 'L'},
    ORG:     {label: 'Org',          desc: 'Organization — companies, agencies, government bodies, institutions',                  color: '#4e7a60', bg: '#eef4ef', initial: 'O'},
    PERSON:  {label: 'Person',       desc: 'Person — named individuals, real or historical',                                      color: '#7a6098', bg: '#f3eef8', initial: 'Pr'},
    PER:     {label: 'Person',       desc: 'Person — named individuals, real or historical',                                      color: '#7a6098', bg: '#f3eef8', initial: 'Pr'},
    EVENT:   {label: 'Event',        desc: 'Event — named events such as wars, elections, conferences',                           color: '#a06a40', bg: '#f8f0ea', initial: 'Ev'},
    FAC:     {label: 'Facility',     desc: 'Facility — buildings, airports, highways, bridges',                                   color: '#3d7a70', bg: '#eef4f2', initial: 'F'},
    NORP:    {label: 'Group',        desc: 'Nationality, religious, or political group — e.g. Americans, Sunni Muslims',          color: '#7a6058', bg: '#f4f0ee', initial: 'G'},
    PRODUCT: {label: 'Product',      desc: 'Product — objects, vehicles, weapons, foods and other manufactured items',            color: '#607880', bg: '#eef1f2', initial: 'Pr'},
    LAW:     {label: 'Law',          desc: 'Law — named documents made into laws',                                                color: '#946080', bg: '#f6eef3', initial: 'La'},
    DATE:    {label: 'Date',         desc: 'Date — absolute or relative dates or periods',                                        color: '#7a8a90', bg: '#eef1f2', initial: 'D'},
    TIME:    {label: 'Time',         desc: 'Time — times smaller than a day',                                                     color: '#7a8a90', bg: '#eef1f2', initial: 'T'},
    MONEY:   {label: 'Money',        desc: 'Money — monetary values, including unit',                                             color: '#7a8a90', bg: '#eef1f2', initial: '$'},
    QUANTITY:{label: 'Quantity',     desc: 'Quantity — measurements, as of weight or distance',                                   color: '#7a8a90', bg: '#eef1f2', initial: '#'},
    UNKNOWN: {label: 'Unknown',      desc: 'Entity type could not be classified by the NER pipeline',                             color: '#9e9e9e', bg: '#f5f5f5', initial: '?'},
};

export const DEFAULT_META = {label: null, desc: null, color: '#546e7a', bg: '#eceff1', initial: '?'};

/** True when the stored type is absent or the NER {@code UNKNOWN} placeholder. */
export function isPlaceholderEntityType(entityType) {
    if (entityType == null || entityType === '') return true;
    return String(entityType).trim().toUpperCase() === 'UNKNOWN';
}

/** First candidate that is a real NER type (skips null/blank/UNKNOWN). */
export function coalesceEntityType(...candidates) {
    for (const candidate of candidates) {
        if (!isPlaceholderEntityType(candidate)) {
            return String(candidate).trim();
        }
    }
    return null;
}

/**
 * Returns display metadata for a NER entity type string.
 * Falls back gracefully for unknown types.
 */
export function getEntityTypeMeta(entityType) {
    if (!entityType) return DEFAULT_META;
    return TYPE_META[entityType.toUpperCase()] || {...DEFAULT_META, label: entityType, desc: entityType};
}

/**
 * Returns the friendly label for a NER entity type string
 * (e.g. "GPE" → "Place", "EVENT" → "Event").
 * Returns the raw string if the type is unknown.
 */
export function getEntityTypeLabel(entityType) {
    if (!entityType) return null;
    return getEntityTypeMeta(entityType).label || entityType;
}

/**
 * Returns the tooltip description for a NER entity type string.
 */
export function getEntityTypeDesc(entityType) {
    if (!entityType) return null;
    return getEntityTypeMeta(entityType).desc || entityType;
}

/** MUI Chip `color` keys aligned with the Knowledge Base Sources / entity list. */
const CHIP_COLOR_MAP = {
    GPE:     'info',
    LOC:     'info',
    ORG:     'success',
    PERSON:  'secondary',
    PER:     'secondary',
    EVENT:   'warning',
    FAC:     'primary',
    NORP:    'default',
    PRODUCT: 'default',
    LAW:     'secondary',
    DATE:    'default',
    TIME:    'default',
    MONEY:   'default',
    QUANTITY:'default',
};

export function getEntityTypeChipColor(entityType) {
    if (!entityType) return 'default';
    return CHIP_COLOR_MAP[String(entityType).trim().toUpperCase()] || 'default';
}
